import dayjs from 'dayjs';

import { DATE_KEY_FORMAT } from '@/helpers';
import { GroupBy } from '@/modules/common/types';

import { logPlannerNavigation } from '../helpers/log';
import { getBufferedVisibleViewportPeriod, getVisibleViewportRange } from '../helpers/viewport';
import { PlannerSliceCreator, QueryRangesSlice } from '../types';

/**
 * Grouping mode + per-group query pagination state. Each GroupBy tab keeps its
 * own loaded date range and scroll-to-date trigger so switching tabs can reuse
 * cached data instead of refetching.
 */
export const createQueryRangesSlice: PlannerSliceCreator<QueryRangesSlice> = set => ({
  groupBy: GroupBy.USERS,
  queryDateRanges: {
    [GroupBy.USERS]: null,
    [GroupBy.DEPARTMENTS]: null,
    [GroupBy.PROJECTS]: null,
  },
  scrollToDateTriggers: {
    [GroupBy.USERS]: 0,
    [GroupBy.DEPARTMENTS]: 0,
    [GroupBy.PROJECTS]: 0,
  },

  setGroupBy: groupBy =>
    set(state => {
      const firstVisibleDate = state.firstVisibleDate || dayjs();
      const targetRange = state.queryDateRanges[groupBy];
      const visibleViewportRange = getVisibleViewportRange(
        state.dates,
        state.visibleDateRange,
        firstVisibleDate
      );
      const visibleStartDateStr = visibleViewportRange.startDate.format(DATE_KEY_FORMAT);
      const visibleEndDateStr = visibleViewportRange.endDate.format(DATE_KEY_FORMAT);

      // If the target group already has a loaded range that covers the currently visible
      // viewport, keep using its cached data. Checking only firstVisibleDate is not enough:
      // a revisited tab can cover the left edge while still missing the visible right side.
      if (targetRange) {
        const isWithinRange =
          visibleStartDateStr >= targetRange.startDate && visibleEndDateStr <= targetRange.endDate;

        if (isWithinRange) {
          logPlannerNavigation('setGroupBy', {
            target: groupBy,
            coveredViewport: `${visibleStartDateStr}..${visibleEndDateStr}`,
            decision: 'range covers viewport, skipping refetch',
          });
          return { groupBy };
        }
      }

      // When switching groups, always bootstrap the fetch from the currently visible
      // viewport span. This keeps revisited tabs in sync with what the user can already
      // see on screen instead of waiting for a follow-up scroll to extend the range.
      const bufferedPeriod = getBufferedVisibleViewportPeriod(
        state.dates,
        state.visibleDateRange,
        firstVisibleDate
      );

      logPlannerNavigation('setGroupBy', {
        target: groupBy,
        viewport: `${visibleStartDateStr}..${visibleEndDateStr}`,
        newRange: `${bufferedPeriod.startDate}..${bufferedPeriod.endDate}`,
      });

      // Only increment the trigger when the tab already has cached data that needs
      // to be replaced (targetRange !== null). When the tab has never been loaded
      // (targetRange === null), the hook hasn't mounted yet and TanStack Query will
      // fire its own first fetch naturally on mount — adding a resetQueries trigger
      // on top would cause a duplicate API call with the same payload.
      const needsResetTrigger = targetRange !== null;

      return {
        groupBy,
        queryDateRanges: {
          ...state.queryDateRanges,
          [groupBy]: bufferedPeriod,
        },
        ...(needsResetTrigger && {
          scrollToDateTriggers: {
            ...state.scrollToDateTriggers,
            [groupBy]: state.scrollToDateTriggers[groupBy] + 1,
          },
        }),
      };
    }),
});
